import React, { useEffect, useState } from 'react';
import { FileText, ExternalLink, AlertCircle } from 'lucide-react';
import { getPublishedBlogsByProfileId } from '../../../../services/firestoreService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * BlogEditor - Editor component for the blog block
 * Shows status info and link to manage blogs
 */
const BlogEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [blogCount, setBlogCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogInfo = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        // Get published blog count
        const blogs = await getPublishedBlogsByProfileId(profileId);
        setBlogCount(blogs?.length || 0);
      } catch (error) {
        console.error('Error loading blog info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogInfo();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <div className="animate-spin w-5 h-5 border-2 border-gray-600 border-t-[#749555] rounded-full mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <FileText size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Blog Posts</h4>
            {blogCount > 0 ? (
              <p className="text-sm text-gray-400">
                {blogCount} published {blogCount === 1 ? 'post' : 'posts'} will display here
              </p>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertCircle size={14} />
                <span>No published blogs yet. Create your first post!</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Latest Posts"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Max Posts */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Maximum Posts to Show
        </label>
        <select
          value={content.maxPosts || 3}
          onChange={(e) => handleChange('maxPosts', parseInt(e.target.value))}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none"
        >
          <option value={1}>1 post</option>
          <option value={2}>2 posts</option>
          <option value={3}>3 posts</option>
          <option value={4}>4 posts</option>
          <option value={6}>6 posts</option>
        </select>
      </div>

      {/* Show View All Toggle */}
      <div className="flex items-center justify-between bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-white">Show "View All" Link</p>
          <p className="text-xs text-gray-500">Links to your full blog page</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.showViewAll !== false}
            onChange={(e) => handleChange('showViewAll', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#749555]"></div>
        </label>
      </div>

      {/* Manage Blogs Link */}
      <a
        href="/dashboard/website/blogs"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Manage Blogs
        <ExternalLink size={14} />
      </a>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['blog']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['blog']}
        theme={theme || {}}
        blockType="blog"
      />
      </>)}
    </div>
  );
};

export default BlogEditor;
